import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Badge,
  Tooltip,
  Collapse,
  Container,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Refresh as RefreshIcon,
  Block as BlockIcon,
  CheckCircle as UnblockIcon,
  Send as SendIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  Code as CodeIcon,
  Timeline as TimelineIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Folder as FolderIcon,
  BarChart as BarChartIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import trackingService from '../../services/trackingService';
import ClaudeUsageTab from './ClaudeUsageTab';
import HackathonScheduleTab from './HackathonScheduleTab';
import TeamLogsTab from './TeamLogsTab';
import HackathonManagementTab from './HackathonManagementTab';

// Design System Colors
const colors = {
  primary: '#485869',
  primaryLight: '#6c7c8c',
  primaryDark: '#3A4754',
  success: '#66CC33',
  error: '#fa5b5b',
  warning: '#FF9933',
  info: '#4DBCF3',
  background: '#F5F6F7',
  paper: '#FFFFFF',
  textPrimary: '#485869',
  textSecondary: '#6B757F',
  grey50: '#F5F6F7',
  grey200: '#C8CDD2',
  grey500: '#6B757F',
  grey700: '#485869',
};

// Stats Card Component - Following Ailyce Design
const StatCard = ({ title, value, icon, color = colors.primary }) => (
  <Card
    elevation={2}
    sx={{
      height: '100%',
      bgcolor: colors.paper,
      borderRadius: 2,
      transition: 'box-shadow 0.2s',
      '&:hover': {
        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)'
      }
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 500 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ color, fontWeight: 700, mt: 1 }}>
            {value}
          </Typography>
        </Box>
        <Box sx={{ color, opacity: 0.6, fontSize: 40 }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// User Row Component
const UserRow = ({ user, onBlock, onUnblock, onViewSessions }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TableRow
        sx={{
          '&:hover': { bgcolor: colors.grey50 },
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <TableCell sx={{ width: 50 }}>
          <IconButton size="small" sx={{ color: colors.textSecondary }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ color: colors.primary }} />
            <Typography variant="body2" fontWeight={600} color={colors.textPrimary}>
              {user.name}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color={colors.textSecondary}>{user.email}</Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={user.is_blocked ? 'Blocked' : 'Active'}
            size="small"
            sx={{
              bgcolor: user.is_blocked ? colors.error : colors.success,
              color: 'white',
              fontWeight: 600,
              fontSize: '0.75rem'
            }}
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" color={colors.textPrimary}>{user.total_sessions}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color={colors.textPrimary}>{user.total_events}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color={colors.textSecondary}>
            {user.last_active ? new Date(user.last_active).toLocaleString() : 'Never'}
          </Typography>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="View Sessions">
              <IconButton
                size="small"
                sx={{ color: colors.info }}
                onClick={(e) => {
                  e.stopPropagation();
                  onViewSessions(user.email);
                }}
              >
                <TimelineIcon />
              </IconButton>
            </Tooltip>
            {user.is_blocked ? (
              <Tooltip title="Unblock User">
                <IconButton
                  size="small"
                  sx={{ color: colors.success }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnblock(user.email);
                  }}
                >
                  <UnblockIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Block User">
                <IconButton
                  size="small"
                  sx={{ color: colors.error }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onBlock(user.email);
                  }}
                >
                  <BlockIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ p: 0, borderBottom: expanded ? `1px solid ${colors.grey200}` : 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: colors.grey50 }}>
              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 1 }}>
                <strong>Device ID:</strong> {user.device_id}
              </Typography>
              {user.recent_project && (
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  <FolderIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  <strong>Recent Project:</strong> {user.recent_project}
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// Session Row Component
const SessionRow = ({ session, onViewEvents }) => {
  const duration = session.ended_at
    ? Math.round((new Date(session.ended_at) - new Date(session.started_at)) / 60000)
    : 'Ongoing';

  return (
    <TableRow
      sx={{
        '&:hover': { bgcolor: colors.grey50 },
        cursor: 'pointer'
      }}
      onClick={() => onViewEvents(session.session_id)}
    >
      <TableCell>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: colors.textSecondary }}>
          {session.session_id.substring(0, 8)}...
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={600} color={colors.textPrimary}>
          {session.user_name || session.user_email}
        </Typography>
      </TableCell>
      <TableCell sx={{ maxWidth: 200 }}>
        <Typography variant="body2" color={colors.textSecondary} noWrap title={session.project_dir}>
          {session.project_dir || 'Unknown'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color={colors.textSecondary}>
          {new Date(session.started_at).toLocaleString()}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={typeof duration === 'number' ? `${duration} min` : duration}
          size="small"
          sx={{
            bgcolor: typeof duration === 'number' ? colors.grey200 : colors.success,
            color: typeof duration === 'number' ? colors.textPrimary : 'white',
            fontWeight: 500,
            fontSize: '0.75rem'
          }}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2" color={colors.textPrimary}>{session.events_count}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color={colors.textPrimary}>{session.prompts_count}</Typography>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {(session.tools_used || []).slice(0, 3).map((tool) => (
            <Chip
              key={tool}
              label={tool}
              size="small"
              sx={{ bgcolor: `${colors.info}20`, color: colors.info, fontSize: '0.7rem', fontWeight: 500 }}
            />
          ))}
          {(session.tools_used || []).length > 3 && (
            <Chip
              label={`+${session.tools_used.length - 3}`}
              size="small"
              sx={{ bgcolor: colors.grey200, color: colors.textSecondary, fontSize: '0.7rem' }}
            />
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

// Event Row Component
const EventRow = ({ event }) => {
  const [expanded, setExpanded] = useState(false);

  // Check if row has any expandable content
  const hasAdditionalData = event.additional_data && Object.keys(event.additional_data).length > 0;
  const hasDetails = event.tool_input || event.prompt || event.tool_response || hasAdditionalData;

  // For Stop events, show response preview in Details column
  const getDetailsPreview = () => {
    if (event.prompt) {
      return event.prompt.substring(0, 100) + (event.prompt.length > 100 ? '...' : '');
    }
    if (event.tool_response) {
      const resp = typeof event.tool_response === 'string' ? event.tool_response : JSON.stringify(event.tool_response);
      return resp.substring(0, 100) + (resp.length > 100 ? '...' : '');
    }
    if (event.file_path) {
      return event.file_path;
    }
    return '-';
  };

  return (
    <>
      <TableRow
        sx={{
          bgcolor: colors.paper,
          '&:hover': { bgcolor: colors.grey50 },
          cursor: hasDetails ? 'pointer' : 'default'
        }}
        onClick={() => hasDetails && setExpanded(!expanded)}
      >
        <TableCell>
          <Typography variant="body2" color={colors.textSecondary} sx={{ fontSize: '0.85rem' }}>
            {new Date(event.timestamp).toLocaleTimeString()}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={event.event_type}
            size="small"
            variant="outlined"
            sx={{
              borderColor: colors.grey200,
              color: colors.textPrimary,
              fontWeight: 600,
              fontSize: '0.7rem'
            }}
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" color={colors.textPrimary}>
            {event.tool_name || '-'}
          </Typography>
        </TableCell>
        <TableCell sx={{ maxWidth: 300 }}>
          <Typography variant="body2" color={colors.textSecondary} noWrap>
            {getDetailsPreview()}
          </Typography>
        </TableCell>
        <TableCell>
          {event.was_auto_approved && (
            <Chip
              label="Auto"
              size="small"
              sx={{ bgcolor: colors.warning, color: 'white', fontWeight: 600, fontSize: '0.7rem' }}
            />
          )}
        </TableCell>
      </TableRow>
      {hasDetails && (
        <TableRow>
          <TableCell colSpan={5} sx={{ p: 0, borderBottom: expanded ? `1px solid ${colors.grey200}` : 0 }}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ p: 2, bgcolor: colors.paper, borderTop: `1px solid ${colors.grey200}` }}>
                {event.prompt && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: colors.textPrimary, mb: 1, fontWeight: 700 }}>
                      Prompt:
                    </Typography>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: colors.grey50, maxHeight: 200, overflow: 'auto', border: `1px solid ${colors.grey200}` }}>
                      <Typography variant="body2" sx={{ color: colors.textPrimary, whiteSpace: 'pre-wrap' }}>
                        {event.prompt}
                      </Typography>
                    </Paper>
                  </Box>
                )}
                {event.tool_input && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: colors.textPrimary, mb: 1, fontWeight: 700 }}>
                      Tool Input:
                    </Typography>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: colors.grey50, maxHeight: 200, overflow: 'auto', border: `1px solid ${colors.grey200}` }}>
                      <pre style={{ margin: 0, color: colors.textPrimary, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                        {JSON.stringify(event.tool_input, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                )}
                {event.tool_response && (
                  <Box sx={{ mb: hasAdditionalData ? 2 : 0 }}>
                    <Typography variant="subtitle2" sx={{ color: colors.textPrimary, mb: 1, fontWeight: 700 }}>
                      Response:
                    </Typography>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: colors.grey50, maxHeight: 300, overflow: 'auto', border: `1px solid ${colors.grey200}` }}>
                      <pre style={{ margin: 0, color: colors.textPrimary, fontSize: '0.8rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {typeof event.tool_response === 'string' ? event.tool_response : JSON.stringify(event.tool_response, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                )}
                {hasAdditionalData && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ color: colors.textPrimary, mb: 1, fontWeight: 700 }}>
                      Additional Data:
                    </Typography>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: colors.grey50, maxHeight: 200, overflow: 'auto', border: `1px solid ${colors.grey200}` }}>
                      <pre style={{ margin: 0, color: colors.textPrimary, fontSize: '0.8rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {JSON.stringify(event.additional_data, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

// Event Block Component - Groups UserPromptSubmit → Tool Events → Stop
const EventBlock = ({ block, blockIndex }) => {
  const [expanded, setExpanded] = useState(false);
  const { startEvent, toolEvents, stopEvent } = block;

  // Calculate block duration
  const startTime = new Date(startEvent.timestamp);
  const endTime = stopEvent ? new Date(stopEvent.timestamp) : null;
  const duration = endTime ? Math.round((endTime - startTime) / 1000) : null;

  // Get prompt preview
  const promptPreview = startEvent.prompt
    ? startEvent.prompt.substring(0, 150) + (startEvent.prompt.length > 150 ? '...' : '')
    : 'No prompt';

  // Get response preview from Stop event
  const responsePreview = stopEvent?.tool_response
    ? (typeof stopEvent.tool_response === 'string'
        ? stopEvent.tool_response
        : JSON.stringify(stopEvent.tool_response)).substring(0, 100) + '...'
    : null;

  return (
    <Box sx={{ mb: 2 }}>
      {/* Block Header */}
      <Paper
        elevation={1}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${colors.grey200}`,
          '&:hover': { borderColor: colors.primary }
        }}
      >
        {/* Main Block Row */}
        <Box
          sx={{
            p: 2,
            bgcolor: colors.grey50,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: expanded ? `1px solid ${colors.grey200}` : 'none'
          }}
          onClick={() => setExpanded(!expanded)}
        >
          {/* Expand Icon */}
          <IconButton size="small" sx={{ color: colors.textSecondary }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>

          {/* Block Number */}
          <Chip
            label={`#${blockIndex + 1}`}
            size="small"
            sx={{
              bgcolor: colors.primary,
              color: 'white',
              fontWeight: 700,
              minWidth: 45
            }}
          />

          {/* Time */}
          <Typography variant="body2" sx={{ color: colors.textSecondary, minWidth: 80 }}>
            {startTime.toLocaleTimeString()}
          </Typography>

          {/* Event Type Badge */}
          <Chip
            label="UserPromptSubmit"
            size="small"
            sx={{
              bgcolor: colors.info,
              color: 'white',
              fontWeight: 600,
              fontSize: '0.7rem'
            }}
          />

          {/* Tool Count */}
          <Chip
            label={`${toolEvents.length} tools`}
            size="small"
            variant="outlined"
            sx={{
              borderColor: colors.warning,
              color: colors.warning,
              fontWeight: 600,
              fontSize: '0.7rem'
            }}
          />

          {/* Duration */}
          {duration !== null && (
            <Chip
              label={`${duration}s`}
              size="small"
              variant="outlined"
              sx={{
                borderColor: colors.success,
                color: colors.success,
                fontWeight: 600,
                fontSize: '0.7rem'
              }}
            />
          )}

          {/* Status */}
          <Chip
            label={stopEvent ? 'Completed' : 'In Progress'}
            size="small"
            sx={{
              bgcolor: stopEvent ? colors.success : colors.warning,
              color: 'white',
              fontWeight: 600,
              fontSize: '0.7rem',
              ml: 'auto'
            }}
          />
        </Box>

        {/* Prompt Preview (always visible) */}
        <Box sx={{ px: 2, py: 1.5, bgcolor: colors.paper }}>
          <Typography variant="body2" sx={{ color: colors.textPrimary, fontStyle: 'italic' }}>
            "{promptPreview}"
          </Typography>
        </Box>

        {/* Expanded Content */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ bgcolor: colors.paper }}>
            {/* Full Prompt */}
            {startEvent.prompt && (
              <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${colors.grey200}` }}>
                <Typography variant="subtitle2" sx={{ color: colors.textPrimary, mb: 1, fontWeight: 700 }}>
                  Full Prompt:
                </Typography>
                <Paper elevation={0} sx={{ p: 2, bgcolor: colors.grey50, maxHeight: 200, overflow: 'auto', border: `1px solid ${colors.grey200}` }}>
                  <Typography variant="body2" sx={{ color: colors.textPrimary, whiteSpace: 'pre-wrap' }}>
                    {startEvent.prompt}
                  </Typography>
                </Paper>
              </Box>
            )}

            {/* Tool Events */}
            {toolEvents.length > 0 && (
              <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${colors.grey200}` }}>
                <Typography variant="subtitle2" sx={{ color: colors.textPrimary, mb: 1, fontWeight: 700 }}>
                  Tool Calls ({toolEvents.length}):
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: colors.grey50 }}>
                        <TableCell sx={{ fontWeight: 600, color: colors.textSecondary, width: 80 }}>Time</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: colors.textSecondary, width: 100 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>Tool</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>Details</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: colors.textSecondary, width: 60 }}>Auto</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {toolEvents.map((event, idx) => (
                        <ToolEventRow key={event.id || idx} event={event} />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Stop Event / Response */}
            {stopEvent && (
              <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${colors.grey200}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
                    Response:
                  </Typography>
                  <Chip
                    label="Stop"
                    size="small"
                    sx={{
                      bgcolor: colors.success,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.65rem'
                    }}
                  />
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                    {new Date(stopEvent.timestamp).toLocaleTimeString()}
                  </Typography>
                </Box>
                {stopEvent.tool_response && (
                  <Paper elevation={0} sx={{ p: 2, bgcolor: colors.grey50, maxHeight: 300, overflow: 'auto', border: `1px solid ${colors.grey200}` }}>
                    <pre style={{ margin: 0, color: colors.textPrimary, fontSize: '0.8rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {typeof stopEvent.tool_response === 'string' ? stopEvent.tool_response : JSON.stringify(stopEvent.tool_response, null, 2)}
                    </pre>
                  </Paper>
                )}
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

// Tool Event Row (for nested display inside blocks)
const ToolEventRow = ({ event }) => {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = event.tool_input || event.tool_response || event.file_path;

  const getTypeColor = () => {
    if (event.event_type === 'PreToolUse') return colors.info;
    if (event.event_type === 'PostToolUse') return colors.success;
    return colors.grey500;
  };

  const getDetailsPreview = () => {
    if (event.file_path) return event.file_path;
    if (event.tool_input) {
      const input = typeof event.tool_input === 'string' ? event.tool_input : JSON.stringify(event.tool_input);
      return input.substring(0, 60) + (input.length > 60 ? '...' : '');
    }
    return '-';
  };

  return (
    <>
      <TableRow
        sx={{
          bgcolor: event.event_type === 'PreToolUse' ? 'rgba(77, 188, 243, 0.05)' : 'rgba(102, 204, 51, 0.05)',
          '&:hover': { bgcolor: colors.grey50 },
          cursor: hasDetails ? 'pointer' : 'default'
        }}
        onClick={() => hasDetails && setExpanded(!expanded)}
      >
        <TableCell>
          <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '0.75rem' }}>
            {new Date(event.timestamp).toLocaleTimeString()}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={event.event_type === 'PreToolUse' ? 'Pre' : 'Post'}
            size="small"
            sx={{
              bgcolor: getTypeColor(),
              color: 'white',
              fontWeight: 600,
              fontSize: '0.65rem',
              height: 20
            }}
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 500 }}>
            {event.tool_name || '-'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '0.8rem' }} noWrap>
            {getDetailsPreview()}
          </Typography>
        </TableCell>
        <TableCell>
          {event.was_auto_approved && (
            <Chip
              label="Auto"
              size="small"
              sx={{ bgcolor: colors.warning, color: 'white', fontWeight: 600, fontSize: '0.6rem', height: 18 }}
            />
          )}
        </TableCell>
      </TableRow>
      {hasDetails && expanded && (
        <TableRow>
          <TableCell colSpan={5} sx={{ p: 0 }}>
            <Box sx={{ p: 1.5, bgcolor: colors.grey50 }}>
              {event.tool_input && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600 }}>Input:</Typography>
                  <Paper elevation={0} sx={{ p: 1, bgcolor: colors.paper, mt: 0.5, maxHeight: 150, overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: '0.7rem', fontFamily: 'monospace' }}>
                      {JSON.stringify(event.tool_input, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}
              {event.tool_response && (
                <Box>
                  <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600 }}>Response:</Typography>
                  <Paper elevation={0} sx={{ p: 1, bgcolor: colors.paper, mt: 0.5, maxHeight: 150, overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: '0.7rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                      {typeof event.tool_response === 'string' ? event.tool_response : JSON.stringify(event.tool_response, null, 2)}
                    </pre>
                  </Paper>
                </Box>
              )}
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

// Helper function to group events into blocks
const groupEventsIntoBlocks = (events) => {
  const blocks = [];
  let currentBlock = null;

  for (const event of events) {
    if (event.event_type === 'UserPromptSubmit') {
      // Start a new block
      if (currentBlock) {
        blocks.push(currentBlock);
      }
      currentBlock = {
        startEvent: event,
        toolEvents: [],
        stopEvent: null
      };
    } else if (event.event_type === 'Stop') {
      // End the current block
      if (currentBlock) {
        currentBlock.stopEvent = event;
        blocks.push(currentBlock);
        currentBlock = null;
      } else {
        // Orphan Stop event - create a standalone block
        blocks.push({
          startEvent: { ...event, event_type: 'UserPromptSubmit', prompt: '(Session event)' },
          toolEvents: [],
          stopEvent: event
        });
      }
    } else if (event.event_type === 'PreToolUse' || event.event_type === 'PostToolUse') {
      // Add to current block's tool events
      if (currentBlock) {
        currentBlock.toolEvents.push(event);
      } else {
        // Orphan tool event - create a new block for it
        currentBlock = {
          startEvent: { timestamp: event.timestamp, event_type: 'UserPromptSubmit', prompt: '(Orphan tool call)' },
          toolEvents: [event],
          stopEvent: null
        };
      }
    } else if (event.event_type === 'SessionStart' || event.event_type === 'SessionEnd') {
      // Session events are standalone
      blocks.push({
        startEvent: { ...event, prompt: event.event_type === 'SessionStart' ? 'Session Started' : 'Session Ended' },
        toolEvents: [],
        stopEvent: null,
        isSessionEvent: true
      });
    }
  }

  // Don't forget the last block if it wasn't closed
  if (currentBlock) {
    blocks.push(currentBlock);
  }

  return blocks;
};

// Main Dashboard Component
const TrackingDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Data
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [events, setEvents] = useState([]);
  const [alerts, setAlerts] = useState([]);

  // Selected items
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);

  // Dialogs
  const [blockDialog, setBlockDialog] = useState({ open: false, email: '' });
  const [blockReason, setBlockReason] = useState('');
  const [broadcastDialog, setBroadcastDialog] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastType, setBroadcastType] = useState('hackathon');
  const [broadcastEmoji, setBroadcastEmoji] = useState('🚀');
  const [broadcastPriority, setBroadcastPriority] = useState('high');
  const [broadcastEndHours, setBroadcastEndHours] = useState(48);
  const [broadcasts, setBroadcasts] = useState([]);
  const [broadcastLoading, setBroadcastLoading] = useState(false);

  // Admin Management
  const [admins, setAdmins] = useState([]);
  const [createAdminDialog, setCreateAdminDialog] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ email: '', name: '', password: '' });
  const [adminError, setAdminError] = useState('');
  const [adminSuccess, setAdminSuccess] = useState('');

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [statsData, usersData, sessionsData, alertsData] = await Promise.all([
        trackingService.getStats(),
        trackingService.getUsers(),
        trackingService.getSessions(null, 20),
        trackingService.getAlerts()
      ]);

      setStats(statsData);
      setUsers(usersData);
      setSessions(sessionsData);
      setAlerts(alertsData);
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error('Error fetching tracking data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!trackingService.isAuthenticated()) {
      navigate('/tracking/login');
      return;
    }

    fetchData();
    fetchAdmins();
    fetchBroadcasts();

    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [navigate, fetchData]);

  // Handle logout
  const handleLogout = () => {
    trackingService.logout();
    navigate('/tracking/login');
  };

  // Handle block user
  const handleBlockUser = async () => {
    if (!blockReason.trim()) {
      return;
    }

    try {
      await trackingService.blockUser(blockDialog.email, blockReason);
      setBlockDialog({ open: false, email: '' });
      setBlockReason('');
      fetchData();
    } catch (err) {
      setError('Failed to block user');
    }
  };

  // Handle unblock user
  const handleUnblockUser = async (email) => {
    try {
      await trackingService.unblockUser(email);
      fetchData();
    } catch (err) {
      setError('Failed to unblock user');
    }
  };

  // Handle broadcast
  const handleBroadcast = async () => {
    if (!broadcastMessage.trim()) {
      return;
    }

    try {
      setBroadcastLoading(true);
      // Save to MongoDB broadcast collection for status line
      await trackingService.createBroadcast(
        broadcastMessage,
        broadcastType,
        broadcastPriority,
        broadcastEmoji,
        broadcastEndHours
      );
      // Also send via legacy tracking system
      await trackingService.broadcastMessage(broadcastMessage);

      setBroadcastDialog(false);
      setBroadcastMessage('');
      setBroadcastType('hackathon');
      setBroadcastEmoji('🚀');
      setBroadcastPriority('high');
      setBroadcastEndHours(48);
      // Refresh broadcasts list
      fetchBroadcasts();
    } catch (err) {
      setError('Failed to send broadcast: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBroadcastLoading(false);
    }
  };

  // Fetch broadcasts
  const fetchBroadcasts = async () => {
    try {
      const data = await trackingService.getBroadcasts();
      setBroadcasts(data.messages || []);
    } catch (err) {
      console.error('Failed to fetch broadcasts:', err);
    }
  };

  // Delete broadcast
  const handleDeleteBroadcast = async (messageId) => {
    try {
      await trackingService.deleteBroadcast(messageId);
      fetchBroadcasts();
    } catch (err) {
      setError('Failed to delete broadcast');
    }
  };

  // Handle create admin
  const handleCreateAdmin = async () => {
    setAdminError('');
    setAdminSuccess('');

    // Validate inputs
    if (!newAdmin.email || !newAdmin.name || !newAdmin.password) {
      setAdminError('All fields are required');
      return;
    }

    if (newAdmin.password.length < 8) {
      setAdminError('Password must be at least 8 characters');
      return;
    }

    try {
      const result = await trackingService.createAdmin(
        newAdmin.email,
        newAdmin.name,
        newAdmin.password
      );

      if (result.success) {
        setAdminSuccess(`Admin ${newAdmin.email} created successfully!`);
        setNewAdmin({ email: '', name: '', password: '' });
        // Fetch updated admin list
        fetchAdmins();
        // Close dialog after 2 seconds
        setTimeout(() => {
          setCreateAdminDialog(false);
          setAdminSuccess('');
        }, 2000);
      } else {
        setAdminError(result.message || 'Failed to create admin');
      }
    } catch (err) {
      setAdminError(err.response?.data?.detail || 'Failed to create admin');
    }
  };

  // Fetch admins list
  const fetchAdmins = async () => {
    try {
      const adminsData = await trackingService.getAdmins();
      setAdmins(adminsData || []);
    } catch (err) {
      console.error('Failed to fetch admins:', err);
      // Fallback to current admin if API fails
      const currentAdmin = trackingService.getCurrentAdmin();
      if (currentAdmin) {
        setAdmins([currentAdmin]);
      }
    }
  };

  // View sessions for a user
  const handleViewUserSessions = async (email) => {
    setSelectedUser(email);
    try {
      const sessionsData = await trackingService.getSessions(email, 50);
      setSessions(sessionsData);
      setTab(1);
    } catch (err) {
      setError('Failed to load user sessions');
    }
  };

  // View events for a session
  const handleViewSessionEvents = async (sessionId) => {
    setSelectedSession(sessionId);
    try {
      const eventsData = await trackingService.getSessionEvents(sessionId);
      setEvents(eventsData);
      setTab(2);
    } catch (err) {
      setError('Failed to load session events');
    }
  };

  // Resolve alert
  const handleResolveAlert = async (alertId) => {
    try {
      await trackingService.resolveAlert(alertId);
      fetchData();
    } catch (err) {
      setError('Failed to resolve alert');
    }
  };

  if (loading && !stats) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: colors.background,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress sx={{ color: colors.primary }} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', bgcolor: colors.background, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* App Bar */}
      <AppBar position="static" elevation={1} sx={{ bgcolor: colors.paper, flexShrink: 0 }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, color: colors.primary, fontWeight: 700 }}>
            Ailyce Team Tracking
          </Typography>
          <Badge badgeContent={alerts.length} color="error" sx={{ mr: 2 }}>
            <Tooltip title="Tampering Alerts">
              <IconButton sx={{ color: alerts.length > 0 ? colors.error : colors.grey500 }}>
                <WarningIcon />
              </IconButton>
            </Tooltip>
          </Badge>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => setBroadcastDialog(true)}
            sx={{
              bgcolor: colors.primary,
              '&:hover': { bgcolor: colors.primaryDark },
              mr: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Broadcast
          </Button>
          <IconButton onClick={fetchData} sx={{ color: colors.grey500, mr: 1 }}>
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={handleLogout} sx={{ color: colors.grey500 }}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Main Content with Scroll */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 3,
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: colors.grey200,
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: colors.grey500,
            borderRadius: '4px',
            '&:hover': {
              bgcolor: colors.primary,
            },
          },
        }}
      >
        <Container maxWidth="xl">
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Stats Cards */}
          {stats && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Users"
                  value={stats.total_users}
                  icon={<PersonIcon fontSize="inherit" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Active Today"
                  value={stats.active_users_today}
                  icon={<PersonIcon fontSize="inherit" />}
                  color={colors.success}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Sessions Today"
                  value={stats.sessions_today}
                  icon={<CodeIcon fontSize="inherit" />}
                  color={colors.warning}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Events Today"
                  value={stats.events_today}
                  icon={<TimelineIcon fontSize="inherit" />}
                  color={colors.info}
                />
              </Grid>
            </Grid>
          )}

          {/* Tabs */}
          <Paper elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
            <Tabs
              value={tab}
              onChange={(e, v) => {
                setTab(v);
                if (v === 0) {
                  setSelectedUser(null);
                  fetchData();
                }
                if (v === 1 && !selectedUser) {
                  fetchData();
                }
              }}
              sx={{
                '& .MuiTab-root': {
                  color: colors.textSecondary,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem'
                },
                '& .Mui-selected': { color: colors.primary },
                '& .MuiTabs-indicator': { bgcolor: colors.primary }
              }}
            >
              <Tab label="Users" />
              <Tab label={selectedUser ? `Sessions (${selectedUser})` : 'Sessions'} />
              <Tab label={selectedSession ? `Events (${selectedSession.substring(0, 8)}...)` : 'Events'} />
              <Tab label={`Alerts (${alerts.length})`} />
              <Tab label="Admins" />
              <Tab
                label="Claude Usage"
                icon={<BarChartIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
              <Tab
                label="Hackathon"
                icon={<span style={{ fontSize: 16 }}>🚀</span>}
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
              <Tab
                label="Team Logs"
                icon={<span style={{ fontSize: 16 }}>👥</span>}
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
              <Tab
                label="Hackathon Mgmt"
                icon={<span style={{ fontSize: 16 }}>🏆</span>}
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
            </Tabs>
          </Paper>

          {/* Tab Content */}
          {tab === 0 && (
            <TableContainer
              component={Paper}
              elevation={1}
              sx={{
                borderRadius: 2,
                maxHeight: 'calc(100vh - 320px)',
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: colors.grey200,
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: colors.grey500,
                  borderRadius: '4px',
                  '&:hover': {
                    bgcolor: colors.primary,
                  },
                },
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600, width: 50 }}></TableCell>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>Sessions</TableCell>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>Events</TableCell>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>Last Active</TableCell>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <UserRow
                      key={user.email}
                      user={user}
                      onBlock={(email) => setBlockDialog({ open: true, email })}
                      onUnblock={handleUnblockUser}
                      onViewSessions={handleViewUserSessions}
                    />
                  ))}
                  {users.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ color: colors.textSecondary, py: 6 }}>
                        No users registered yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 1 && (
            <TableContainer
              component={Paper}
              elevation={1}
              sx={{
                borderRadius: 2,
                maxHeight: 'calc(100vh - 320px)',
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: colors.grey200,
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: colors.grey500,
                  borderRadius: '4px',
                  '&:hover': {
                    bgcolor: colors.primary,
                  },
                },
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>Session ID</TableCell>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>User</TableCell>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>Project</TableCell>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>Started</TableCell>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>Duration</TableCell>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>Events</TableCell>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>Prompts</TableCell>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>Tools Used</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.map((session) => (
                    <SessionRow
                      key={session.session_id}
                      session={session}
                      onViewEvents={handleViewSessionEvents}
                    />
                  ))}
                  {sessions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ color: colors.textSecondary, py: 6 }}>
                        No sessions found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 2 && (
            <Box
              sx={{
                maxHeight: 'calc(100vh - 320px)',
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: colors.grey200,
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: colors.grey500,
                  borderRadius: '4px',
                  '&:hover': {
                    bgcolor: colors.primary,
                  },
                },
              }}
            >
              {events.length > 0 ? (
                <>
                  {/* Summary Header */}
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                      {groupEventsIntoBlocks(events).length} conversation blocks from {events.length} events
                    </Typography>
                    <Chip
                      label="Grouped View"
                      size="small"
                      sx={{ bgcolor: colors.info, color: 'white', fontSize: '0.7rem' }}
                    />
                  </Box>

                  {/* Grouped Event Blocks */}
                  {groupEventsIntoBlocks(events).map((block, idx) => (
                    <EventBlock key={idx} block={block} blockIndex={idx} />
                  ))}
                </>
              ) : (
                <Paper elevation={1} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                  <Typography sx={{ color: colors.textSecondary }}>
                    No events found. Select a session to view events.
                  </Typography>
                </Paper>
              )}
            </Box>
          )}

          {tab === 3 && (
            <TableContainer
              component={Paper}
              elevation={1}
              sx={{
                borderRadius: 2,
                maxHeight: 'calc(100vh - 320px)',
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: colors.grey200,
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: colors.grey500,
                  borderRadius: '4px',
                  '&:hover': {
                    bgcolor: colors.primary,
                  },
                },
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>User</TableCell>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>Device</TableCell>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>Detected</TableCell>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>Details</TableCell>
                    <TableCell sx={{ bgcolor: colors.grey50, color: colors.textSecondary, fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {alerts.map((alert) => (
                    <TableRow key={alert.id} sx={{ '&:hover': { bgcolor: colors.grey50 } }}>
                      <TableCell>
                        <Chip
                          label={alert.alert_type}
                          size="small"
                          sx={{ bgcolor: colors.error, color: 'white', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color={colors.textPrimary}>{alert.user_email || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: colors.textSecondary }}>
                          {alert.device_id?.substring(0, 8) || '-'}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color={colors.textSecondary}>
                          {new Date(alert.detected_at).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography variant="body2" color={colors.textSecondary} noWrap>
                          {JSON.stringify(alert.details)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleResolveAlert(alert.id)}
                          sx={{
                            color: colors.primary,
                            borderColor: colors.primary,
                            textTransform: 'none',
                            fontWeight: 600
                          }}
                        >
                          Resolve
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {alerts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: colors.textSecondary, py: 6 }}>
                        No tampering alerts
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Admins Tab */}
          {tab === 4 && (
            <Box>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                  Admin Management
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<PersonIcon />}
                  onClick={() => setCreateAdminDialog(true)}
                  sx={{
                    bgcolor: colors.primary,
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { bgcolor: colors.primaryDark }
                  }}
                >
                  Add New Admin
                </Button>
              </Box>

              <TableContainer
                component={Paper}
                elevation={1}
                sx={{
                  borderRadius: 2,
                  '& .MuiTableCell-head': { fontWeight: 700 }
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: colors.grey50 }}>
                      <TableCell sx={{ color: colors.textPrimary }}>Email</TableCell>
                      <TableCell sx={{ color: colors.textPrimary }}>Name</TableCell>
                      <TableCell sx={{ color: colors.textPrimary }}>Status</TableCell>
                      <TableCell sx={{ color: colors.textPrimary }}>Created At</TableCell>
                      <TableCell sx={{ color: colors.textPrimary }}>Last Login</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {admins.map((admin, index) => (
                      <TableRow key={admin.id || index} sx={{ '&:hover': { bgcolor: colors.grey50 } }}>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 500 }}>
                            {admin.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={colors.textPrimary}>{admin.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={admin.is_active !== false ? 'Active' : 'Inactive'}
                            size="small"
                            sx={{
                              bgcolor: admin.is_active !== false ? `${colors.success}20` : `${colors.error}20`,
                              color: admin.is_active !== false ? colors.success : colors.error,
                              fontWeight: 600
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={colors.textSecondary}>
                            {admin.created_at ? new Date(admin.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            }) : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={colors.textSecondary}>
                            {admin.last_login ? new Date(admin.last_login).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'Never'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {admins.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ color: colors.textSecondary, py: 6 }}>
                          No admins found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 2, p: 2, bgcolor: colors.grey50, borderRadius: 2 }}>
                <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                  <strong>Note:</strong> Only existing admins can create new admin accounts. Each admin has full access to the tracking dashboard.
                </Typography>
              </Box>
            </Box>
          )}

          {/* Claude Usage Tab */}
          {tab === 5 && (
            <ClaudeUsageTab />
          )}

          {/* Hackathon Schedule Tab */}
          {tab === 6 && (
            <HackathonScheduleTab />
          )}

          {/* Team Logs Tab */}
          {tab === 7 && (
            <TeamLogsTab />
          )}

          {/* Hackathon Management Tab */}
          {tab === 8 && (
            <HackathonManagementTab />
          )}
        </Container>
      </Box>

      {/* Block User Dialog */}
      <Dialog
        open={blockDialog.open}
        onClose={() => setBlockDialog({ open: false, email: '' })}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 400 } }}
      >
        <DialogTitle sx={{ color: colors.textPrimary, fontWeight: 700 }}>Block User</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, color: colors.textSecondary }}>
            Block user: <strong>{blockDialog.email}</strong>
          </Typography>
          <TextField
            fullWidth
            label="Reason"
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setBlockDialog({ open: false, email: '' })}
            sx={{ color: colors.textSecondary, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBlockUser}
            variant="contained"
            sx={{
              bgcolor: colors.error,
              '&:hover': { bgcolor: '#d94949' },
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Block User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Broadcast Dialog */}
      <Dialog
        open={broadcastDialog}
        onClose={() => setBroadcastDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ color: colors.textPrimary, fontWeight: 700 }}>
          Broadcast Message
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, color: colors.textSecondary }}>
            Create a broadcast message. It will appear in Claude Code status line for all users.
          </Typography>

          <Grid container spacing={2}>
            {/* Message */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message"
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
                multiline
                rows={2}
                placeholder="e.g., Hackathon starts Dec 20!"
                inputProps={{ maxLength: 100 }}
                helperText={`${broadcastMessage.length}/100 characters`}
              />
            </Grid>

            {/* Type */}
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={broadcastType}
                  onChange={(e) => setBroadcastType(e.target.value)}
                  label="Type"
                >
                  <MenuItem value="hackathon">Hackathon</MenuItem>
                  <MenuItem value="announcement">Announcement</MenuItem>
                  <MenuItem value="alert">Alert</MenuItem>
                  <MenuItem value="info">Info</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Emoji */}
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Emoji</InputLabel>
                <Select
                  value={broadcastEmoji}
                  onChange={(e) => setBroadcastEmoji(e.target.value)}
                  label="Emoji"
                >
                  <MenuItem value="🚀">🚀 Rocket</MenuItem>
                  <MenuItem value="📢">📢 Megaphone</MenuItem>
                  <MenuItem value="🎉">🎉 Party</MenuItem>
                  <MenuItem value="⚠️">⚠️ Warning</MenuItem>
                  <MenuItem value="🏆">🏆 Trophy</MenuItem>
                  <MenuItem value="💻">💻 Laptop</MenuItem>
                  <MenuItem value="⏰">⏰ Clock</MenuItem>
                  <MenuItem value="🔥">🔥 Fire</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Priority */}
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={broadcastPriority}
                  onChange={(e) => setBroadcastPriority(e.target.value)}
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Duration */}
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Expires In</InputLabel>
                <Select
                  value={broadcastEndHours}
                  onChange={(e) => setBroadcastEndHours(e.target.value)}
                  label="Expires In"
                >
                  <MenuItem value={6}>6 hours</MenuItem>
                  <MenuItem value={12}>12 hours</MenuItem>
                  <MenuItem value={24}>24 hours</MenuItem>
                  <MenuItem value={48}>48 hours</MenuItem>
                  <MenuItem value={72}>72 hours</MenuItem>
                  <MenuItem value={168}>1 week</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Preview */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: colors.grey50, borderRadius: 2 }}>
                <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600 }}>
                  STATUS LINE PREVIEW:
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, fontFamily: 'monospace', color: colors.textPrimary }}>
                  {broadcastEmoji} {broadcastMessage || 'Your message here...'} | Model | 0k/200k (0%) | $0.00
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Active Broadcasts */}
          {broadcasts.length > 0 && (
            <Box mt={3}>
              <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 1 }}>
                Active Broadcasts ({broadcasts.length})
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: colors.grey50 }}>
                      <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>Message</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }}>Expires</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: colors.textSecondary }} width={50}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {broadcasts.map((b) => (
                      <TableRow key={b.id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                            {b.emoji} {b.message}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={b.message_type} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                            {b.end_date ? new Date(b.end_date).toLocaleString() : 'Never'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteBroadcast(b.id)}
                            sx={{ color: colors.error }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setBroadcastDialog(false)}
            sx={{ color: colors.textSecondary, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBroadcast}
            variant="contained"
            disabled={!broadcastMessage.trim() || broadcastLoading}
            sx={{
              bgcolor: colors.primary,
              '&:hover': { bgcolor: colors.primaryDark },
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Send Broadcast
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Admin Dialog */}
      <Dialog
        open={createAdminDialog}
        onClose={() => {
          setCreateAdminDialog(false);
          setNewAdmin({ email: '', name: '', password: '' });
          setAdminError('');
          setAdminSuccess('');
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 520,
            maxWidth: 560,
            boxShadow: '0 24px 48px rgba(72, 88, 105, 0.2)',
            overflow: 'hidden'
          }
        }}
      >
        {/* Header with gradient background */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: 'rgba(255, 255, 255, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <PersonIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.2 }}>
              Create New Admin
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mt: 0.5 }}>
              Add a team member with full dashboard access
            </Typography>
          </Box>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {adminError && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-icon': { alignItems: 'center' }
              }}
            >
              {adminError}
            </Alert>
          )}

          {adminSuccess && (
            <Alert
              severity="success"
              sx={{
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-icon': { alignItems: 'center' }
              }}
            >
              {adminSuccess}
            </Alert>
          )}

          {/* Form Fields */}
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={newAdmin.email}
            onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
            placeholder="admin@company.com"
            required
            variant="outlined"
            InputProps={{
              sx: { borderRadius: 2, bgcolor: colors.paper }
            }}
            InputLabelProps={{
              shrink: true
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': { borderColor: colors.primary },
                '&.Mui-focused fieldset': { borderColor: colors.primary, borderWidth: 2 }
              },
              '& .MuiInputLabel-root': {
                color: colors.textSecondary,
                '&.Mui-focused': { color: colors.primary }
              }
            }}
          />

          <TextField
            fullWidth
            label="Full Name"
            value={newAdmin.name}
            onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
            placeholder="John Doe"
            required
            variant="outlined"
            InputProps={{
              sx: { borderRadius: 2, bgcolor: colors.paper }
            }}
            InputLabelProps={{
              shrink: true
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': { borderColor: colors.primary },
                '&.Mui-focused fieldset': { borderColor: colors.primary, borderWidth: 2 }
              },
              '& .MuiInputLabel-root': {
                color: colors.textSecondary,
                '&.Mui-focused': { color: colors.primary }
              }
            }}
          />

          <TextField
            fullWidth
            label="Password"
            type="password"
            value={newAdmin.password}
            onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
            placeholder="Enter a strong password"
            helperText="Minimum 8 characters required"
            required
            variant="outlined"
            InputProps={{
              sx: { borderRadius: 2, bgcolor: colors.paper }
            }}
            InputLabelProps={{
              shrink: true
            }}
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': { borderColor: colors.primary },
                '&.Mui-focused fieldset': { borderColor: colors.primary, borderWidth: 2 }
              },
              '& .MuiInputLabel-root': {
                color: colors.textSecondary,
                '&.Mui-focused': { color: colors.primary }
              },
              '& .MuiFormHelperText-root': {
                color: colors.textSecondary,
                fontSize: '0.75rem',
                ml: 0,
                mt: 1
              }
            }}
          />

          {/* Permissions Info Card */}
          <Box
            sx={{
              mt: 3,
              p: 2.5,
              bgcolor: `${colors.info}08`,
              border: `1px solid ${colors.info}25`,
              borderRadius: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <WarningIcon sx={{ color: colors.info, fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
                Admin Permissions
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 0.5 }}>
              {[
                'View all users and their activities',
                'Block and unblock users',
                'Send broadcast messages',
                'Create additional admin accounts'
              ].map((permission, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: colors.info
                    }}
                  />
                  <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                    {permission}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            pt: 0,
            gap: 1.5
          }}
        >
          <Button
            onClick={() => {
              setCreateAdminDialog(false);
              setNewAdmin({ email: '', name: '', password: '' });
              setAdminError('');
              setAdminSuccess('');
            }}
            sx={{
              color: colors.textSecondary,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
              '&:hover': {
                bgcolor: colors.grey50
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateAdmin}
            variant="contained"
            disabled={!newAdmin.email || !newAdmin.name || !newAdmin.password}
            sx={{
              bgcolor: colors.primary,
              '&:hover': {
                bgcolor: colors.primaryDark,
                boxShadow: '0 4px 12px rgba(72, 88, 105, 0.35)'
              },
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(72, 88, 105, 0.25)',
              transition: 'all 0.2s ease',
              '&.Mui-disabled': {
                bgcolor: colors.grey200,
                color: colors.grey500,
                boxShadow: 'none'
              }
            }}
          >
            Create Admin
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrackingDashboard;
